import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { objectOmit } from '@/_shared/helpers/objectOmit';
import { GenericApiError } from '@/_shared/types/apiError';
import { getCurrentOrganization } from '@/core/state';
import { API_PENDING_PAYMENTS_GET, KEY_GET_PENDING_PAYMENTS } from '../constants';
import { MOCK_RESPONSE_GET_PENDING_PAYMENTS } from '../mocks';
import { GetPendingPaymentsParams, PendingPaymentFromBE, PendingPayments } from '../types';

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsGetPendingPayments, 'successSaga' | 'errorSaga'>;
};

export type CallParamsGetPendingPayments = GetPendingPaymentsParams & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponseGetPendingPayments = {
  data: PendingPaymentFromBE[];
  totalItems: number;
};

type CallErrorGetPendingPayments = GenericApiError;

type CallSelectorsGetPendingPayments = {
  data: PendingPayments[];
  totalItems: number;
  fullRequest: FullRequestType;
};

let internalPayload = {};

export const getPendingPaymentsCall = createGeneralCall<
  CallParamsGetPendingPayments,
  CallResponseGetPendingPayments,
  CallErrorGetPendingPayments,
  CallSelectorsGetPendingPayments
>({
  id: KEY_GET_PENDING_PAYMENTS,
  url:
    ({ payload }) =>
    state => {
      const props = {
        ...objectOmit(payload, ['successSaga', 'errorSaga']),
      };
      const org = getCurrentOrganization(state);
      return API_PENDING_PAYMENTS_GET(props, org as unknown as string);
    },
  method: 'get',
  paramsSelector:
    ({ payload }) =>
    () => {
      internalPayload = { ...payload };
      return payload;
    },
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_PENDING_PAYMENTS : undefined,
  selectors: {
    data: ({ selectors }) =>
      createSelector(selectors.response, res => (res?.data as PendingPayments[]) || []),
    totalItems: ({ selectors }) => createSelector(selectors.response, res => res?.totalItems),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
  },
  responseMapper: ({ response }) => {
    return {
      data:
        response.data?.map(payment => ({ ...payment, currency: payment.currency || 'EUR' })) || [],
      totalItems: response.totalItems || response.data?.length || 0,
    };
  },
});
